import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

const DURATION = 1100;

/** Decelerating curve: fast arrival, gentle settle. */
const easeOut = (t) => 1 - (1 - t) ** 3;

function formatter(mode, digits) {
  if (mode === "percent") return (v) => (v * 100).toFixed(digits);
  if (mode === "fixed") return (v) => v.toFixed(digits);
  const nf = new Intl.NumberFormat();
  return (v) => nf.format(Math.round(v));
}

/**
 * Number that counts up to its value the first time it scrolls into view.
 *
 * Written straight to the DOM node rather than through state: at 60fps a
 * setState per frame per counter would re-render the whole readout grid.
 * Rendered with tabular figures by the caller (`.num` / mono classes) so the
 * width does not jitter while counting. Reduced motion prints the final value.
 */
function Counter({ value, mode = "number", digits = 0, prefix = "", suffix = "", className = "" }) {
  const prefersReducedMotion = useReducedMotion();
  const hostRef = useRef(null);
  const numRef = useRef(null);
  const finite = Number.isFinite(value);
  const safe = finite ? value : 0;

  useEffect(() => {
    const host = hostRef.current;
    const num = numRef.current;
    if (!host || !num || !finite) return undefined;

    const format = formatter(mode, digits);

    if (prefersReducedMotion) {
      num.textContent = format(safe);
      return undefined;
    }

    let raf = 0;
    let observer = null;

    const run = () => {
      const startedAt = performance.now();
      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / DURATION);
        num.textContent = format(safe * easeOut(progress));
        if (progress < 1) raf = requestAnimationFrame(step);
        else num.textContent = format(safe);
      };
      raf = requestAnimationFrame(step);
    };

    num.textContent = format(0);

    observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        run();
      },
      { rootMargin: "0px 0px -8% 0px" }
    );
    observer.observe(host);

    return () => {
      observer?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [safe, finite, mode, digits, prefersReducedMotion]);

  if (!finite) return <span className={className}>—</span>;

  return (
    <span ref={hostRef} className={className}>
      {prefix}
      <span ref={numRef}>{formatter(mode, digits)(prefersReducedMotion ? safe : 0)}</span>
      {suffix}
    </span>
  );
}

export default Counter;
